/**
 * Audit Store - State Management for Audit Logs
 * Following PRD Phase 4 specifications (lines 487-496)
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuditLog, AuditFilters, AuditStats } from '@/types/audit';
import { fetchAuditLogs, fetchAuditLogById, exportAuditLogs } from '@/services/audit';

interface AuditStore {
  // State
  logs: AuditLog[];
  filters: AuditFilters;
  isLoading: boolean;
  hasMore: boolean;
  totalCount: number;
  stats: AuditStats | null;
  selectedLog: AuditLog | null;
  exportProgress: number;
  isExporting: boolean;
  
  // WebSocket
  wsConnection: WebSocket | null;
  isConnected: boolean;
  
  // Actions
  fetchLogs: (append?: boolean) => Promise<void>;
  setFilters: (filters: Partial<AuditFilters>) => void;
  resetFilters: () => void;
  fetchLogById: (id: string) => Promise<void>;
  exportLogs: (format: 'csv' | 'json' | 'pdf') => Promise<void>;
  
  // WebSocket actions
  subscribeToUpdates: () => void;
  unsubscribeFromUpdates: () => void;
  
  // Internal actions
  addLog: (log: AuditLog) => void;
  updateLog: (id: string, updates: Partial<AuditLog>) => void;
  setExportProgress: (progress: number) => void;
}

const defaultFilters: AuditFilters = {
  search: '',
  actions: [],
  status: [],
  dateRange: undefined,
  page: 1,
  limit: 50
};

export const useAuditStore = create<AuditStore>((set, get) => ({
      // Initial state
      logs: [],
      filters: defaultFilters,
      isLoading: false,
      hasMore: true,
      totalCount: 0,
      stats: null,
      selectedLog: null,
      exportProgress: 0,
      isExporting: false,
      wsConnection: null,
      isConnected: false,
      
      // Fetch logs with optional append for pagination
      fetchLogs: async (append = false) => {
        set({ isLoading: true });
        
        try {
          const { filters } = get();
          const response = await fetchAuditLogs(filters);
          
          set({
            logs: append ? [...get().logs, ...response.data] : response.data,
            totalCount: response.total,
            hasMore: response.hasMore,
            stats: response.stats,
            isLoading: false
          });
        } catch (error) {
          console.error('Failed to fetch audit logs:', error);
          set({ isLoading: false });
          // Don't throw error to prevent page from breaking
          // throw error;
        }
      },
      
      // Set filters and reset pagination
      setFilters: (newFilters) => {
        set((state) => ({
          filters: {
            ...state.filters,
            ...newFilters,
            page: 1 // Reset to first page when filters change
          }
        }));
        
        // Automatically refetch when filters change
        get().fetchLogs(false);
      },
      
      // Reset all filters
      resetFilters: () => {
        set({ filters: defaultFilters });
        get().fetchLogs(false);
      },
      
      // Fetch individual log details
      fetchLogById: async (id) => {
        try {
          const log = await fetchAuditLogById(id);
          set({ selectedLog: log });
        } catch (error) {
          console.error('Failed to fetch audit log:', error);
          throw error;
        }
      },
      
      // Export logs with progress tracking
      exportLogs: async (format) => {
        set({ isExporting: true, exportProgress: 0 });
        
        try {
          const { filters } = get();
          
          // Simulate progress for better UX
          const progressInterval = setInterval(() => {
            set((state) => ({
              exportProgress: Math.min(state.exportProgress + 10, 90)
            }));
          }, 200);
          
          const blob = await exportAuditLogs({ format, filters });
          
          clearInterval(progressInterval);
          set({ exportProgress: 100 });
          
          // Create download link
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.${format}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          
          // Reset progress after a delay
          setTimeout(() => {
            set({ exportProgress: 0, isExporting: false });
          }, 1000);
        } catch (error) {
          console.error('Failed to export audit logs:', error);
          set({ exportProgress: 0, isExporting: false });
          throw error;
        }
      },
      
      // WebSocket subscription for real-time updates
      subscribeToUpdates: () => {
        const { wsConnection } = get();
        if (wsConnection) return;

        const ws = new WebSocket(`${import.meta.env.VITE_WS_URL}/api/audit/stream`);
        
        ws.onopen = () => {
          set({ wsConnection: ws, isConnected: true });
          console.log('Connected to audit log stream');
        };
        
        ws.onmessage = (event) => {
          try {
            const log: AuditLog = JSON.parse(event.data);
            get().addLog(log);
          } catch (error) {
            console.error('Failed to parse audit log:', error);
          }
        };
        
        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          set({ isConnected: false });
        };
        
        ws.onclose = () => {
          set({ wsConnection: null, isConnected: false });
          console.log('Disconnected from audit log stream');
          
          // Attempt to reconnect after 5 seconds
          setTimeout(() => {
            const { wsConnection } = get();
            if (!wsConnection) {
              get().subscribeToUpdates();
            }
          }, 5000);
        };
        
        set({ wsConnection: ws });
      },
      
      // Unsubscribe from WebSocket updates
      unsubscribeFromUpdates: () => {
        const { wsConnection } = get();
        if (wsConnection) {
          wsConnection.close();
          set({ wsConnection: null, isConnected: false });
        }
      },
      
      // Add a new log (used by WebSocket)
      addLog: (log) => {
        set((state) => ({
          logs: [log, ...state.logs].slice(0, 100), // Keep max 100 logs in memory
          totalCount: state.totalCount + 1,
          stats: state.stats ? {
            ...state.stats,
            totalLogs: state.stats.totalLogs + 1,
            [log.status === 'success' ? 'successCount' : log.status === 'error' ? 'errorCount' : 'pendingCount']: 
              state.stats[log.status === 'success' ? 'successCount' : log.status === 'error' ? 'errorCount' : 'pendingCount'] + 1
          } : null
        }));
      },
      
      // Update an existing log
      updateLog: (id, updates) => {
        set((state) => ({
          logs: state.logs.map(log => 
            log.id === id ? { ...log, ...updates } : log
          )
        }));
      },
      
      // Set export progress
      setExportProgress: (progress) => {
        set({ exportProgress: progress });
      }
    }));